import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DxButtonModule, DxListModule } from 'devextreme-angular';
import saveAs from 'file-saver';
import Mime from 'mime';
import { map } from 'rxjs';
import { FileDragAndDropDirective } from '../../directives';
import { DialogService } from '../../lib/services';
import { HumanizeBytesPipe } from '../../pipes/humanize-bytes.pipe';
import { LoadIndicatorComponent } from '../load-indicator/load-indicator.component';
import { FileViewModel } from '../../lib/models';

@Component({
    selector: 'app-file-list',
    templateUrl: './file-list.component.html',
    styleUrl: './file-list.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        DxListModule,
        DxButtonModule,
        FileDragAndDropDirective,
        HumanizeBytesPipe,
        TranslatePipe,
        LoadIndicatorComponent,
    ],
})
export class FileListComponent {
    protected readonly http: HttpClient = inject(HttpClient);
    protected readonly dialogs: DialogService = inject(DialogService);
    protected readonly translate: TranslateService = inject(TranslateService);

    showLabel = input<boolean>(true);
    label = input<string>(this.translate.instant('COMMON.FILE_LIST.LABEL_FILES'));

    allowUpload = input<boolean>(true);
    allowDownload = input<boolean>(true);
    allowDelete = input<boolean>(true);
    allowCancelLocal = input<boolean>(true);

    maxTotalFiles = input<number>(3);
    maxTotalBytes = input<number>(5 * 1024 * 1024); // 5 MB

    showRemoteFiles = input<boolean>(true);

    allowedContentTypes = input<string[]>(['application/pdf', 'text/pdf']);

    remoteFiles = model<FileViewModel[]>([]);
    localFiles = model<FileViewModel[]>([]);

    remoteChanged = output<void>();

    $files = signal<FileViewModel[]>([]);

    currentTotalBytes = computed(() =>
        this.$files().reduce((total, file) => total + file.size, 0),
    );
    currentTotalFiles = computed(() => this.$files().length);

    allowedContentTypesString = computed(() =>
        this.allowedContentTypes()
            .map((ct) => Mime.getExtension(ct))
            .filter((ext) => !!ext),
    );

    currentAvailableMegaBytes = computed(
        () => (this.maxTotalBytes() - this.currentTotalBytes()) / (1024 * 1024),
    );

    canUpload = computed(
        () =>
            this.allowUpload() &&
            this.currentTotalFiles() < this.maxTotalFiles() &&
            this.currentTotalBytes() < this.maxTotalBytes(),
    );

    visibleFiles = computed(() =>
        this.$files().filter((f) => this.showRemoteFiles() || !f.isRemote),
    );

    $loadIndicatorVisible = signal<boolean>(false);

    constructor() {
        effect(() => {
            this.$files.set([...this.remoteFiles(), ...this.localFiles()]);
        });
    }

    async onFileDrop(e: File[] | FileList) {
        if (!this.canUpload()) return;

        let totalBytes = this.currentTotalBytes();
        let totalFiles = this.currentTotalFiles();

        const newFiles: FileViewModel[] = [];
        for (let i = 0; i < e.length; i++) {
            const file = e[i];
            if (totalFiles >= this.maxTotalFiles()) {
                break;
            }
            if (!this.allowedContentTypes().includes(file.type)) {
                continue;
            }
            if (totalBytes + file.size > this.maxTotalBytes()) {
                continue;
            }
            totalBytes += file.size;
            totalFiles += 1;
            newFiles.push(new FileViewModel(file));
        }
        this.localFiles.set([...this.localFiles(), ...newFiles]);
    }

    cancelLocalFile(file: FileViewModel) {
        this.localFiles.set(this.localFiles().filter((f) => f !== file));
    }

    downloadFile(file: FileViewModel) {
        if (!file.isRemote || !file.downloadUrl) {
            return;
        }
        this.$loadIndicatorVisible.set(true);

        this.http
            .request(file.downloadMethod, file.downloadUrl, {
                responseType: 'blob',
                observe: 'response',
            })
            .pipe(
                map((res) => {
                    return saveAs(res.body!, file.name);
                }),
            )
            .subscribe(() => {
                this.$loadIndicatorVisible.set(false);
            });
    }

    deleteFile(file: FileViewModel) {
        if (!file.isRemote || !file.deleteUrl) {
            return;
        }
        this.dialogs
            .confirm({
                title: this.translate.instant(
                    'COMMON.FILE_LIST.CONFIRM_DELETE_TITLE',
                ),
                message: this.translate.instant(
                    'COMMON.FILE_LIST.CONFIRM_DELETE_MESSAGE',
                ),
            })
            .subscribe((confirmed) => {
                if (!confirmed) return;
                this.$loadIndicatorVisible.set(true);
                this.http
                    .request(file.deleteMethod, file.deleteUrl!)
                    .subscribe(() => {
                        this.remoteFiles.set(
                            this.remoteFiles().filter((f) => f !== file),
                        );
                        this.remoteChanged.emit();
                        this.$loadIndicatorVisible.set(false);
                    });
            });
    }

    getIconByMIME = (contentType: string) =>
        MIMEIconMap.IconMap[contentType] ||
        new MIMEIconMap(contentType, 'fa-solid fa-file', 'fa');
}



export class MIMEIconMap {
    public static IconMap: { [key: string]: MIMEIconMap } = {
        'application/pdf': new MIMEIconMap('application/pdf', 'pdffile', 'dx'),
        'text/pdf': new MIMEIconMap('text/pdf', 'pdffile', 'dx'),
    };
    constructor(
        public mimeType: string,
        public icon: string,
        public type: 'fa' | 'dx' = 'fa',
    ) { }

    get iconClass() {
        switch (this.type) {
            case 'dx':
                return `dx-icon-${this.icon}`;
            case 'fa':
                return `${this.icon}`;
        }
    }
}
